export type NavigationApp = 'googleMaps' | 'waze';

export type NavigationPoint =
  | { type: 'coordinates'; latitude: number; longitude: number }
  | { type: 'address'; address: string };

export type NavigationDestination =
  | NavigationPoint
  // The origin is best-effort: some apps (Waze) always navigate from the
  // user's current location and ignore it
  | { type: 'route'; origin: NavigationPoint; destination: NavigationPoint };

const encodePoint = (point: NavigationPoint) =>
  point.type === 'coordinates'
    ? `${point.latitude}%2C${point.longitude}`
    : encodeURIComponent(point.address);

const generateGoogleMapsURL = (destination: NavigationDestination) => {
  // https://developers.google.com/maps/documentation/urls/get-started?hl=fr seems to be universal now
  if (destination.type === 'route') {
    return `https://www.google.com/maps/dir/?api=1&origin=${encodePoint(destination.origin)}&destination=${encodePoint(destination.destination)}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodePoint(destination)}`;
};

// https://developers.google.com/waze/deeplinks
const generateWazeURL = (destination: NavigationDestination) => {
  const WAZE_BASE_URI = 'https://www.waze.com/ul?';

  // Waze always navigates from the current location: a route's origin cannot be passed
  const point =
    destination.type === 'route' ? destination.destination : destination;

  return point.type === 'coordinates'
    ? `${WAZE_BASE_URI}ll=${point.latitude}%2C${point.longitude}&navigate=yes`
    : `${WAZE_BASE_URI}q=${encodeURIComponent(point.address)}&navigate=yes`;
};

export const generateNavigationURL = ({
  destination,
  app,
}: {
  destination: NavigationDestination;
  app: NavigationApp;
}) => {
  return app === 'waze'
    ? generateWazeURL(destination)
    : generateGoogleMapsURL(destination);
};

export const getNavigationDestination = ({
  address,
  lat,
  long,
}: {
  lat?: number;
  long?: number;
  address?: string;
}): NavigationDestination | null => {
  if (lat != null && long != null) {
    return { type: 'coordinates', latitude: lat, longitude: long };
  }
  if (address) {
    return { type: 'address', address };
  }
  return null;
};
